// Zustand store — canonical message + tool-trace state for one active thread.
//
// ADR-038 D6 originally bridged this store via runtime/adapter.tsx's
// ChatRuntimeProvider + ExternalStoreRuntime; that wrapper was a pass-through
// stub and got deleted in ADR-095 Phase 5.7. Selectors (useMessages,
// useIsStreaming, useActiveTenantId, useAbort) now live in this file as
// exported hooks. SSE events mutate this state via runtime/ssePump.ts.

import { create } from "zustand";
import { getJob } from "@/api/jobs";
import { isTerminal } from "@/lib/jobStatusBadge";
import type {
  ConsultedDocRef,
  JobSnapshot,
  PersistedCitationRef,
  StreamEvent,
  ToolPlan,
  ToolTrace,
  UnverifiedClaimRef,
  WorkerStatus,
} from "@/types";
import { narrowCitations } from "./citationNarrowing";

export type Role = "user" | "assistant" | "system";

// Exhaustiveness guard for the applyEvent switch. In the `default` arm TS narrows
// `event` to `never` only when every StreamEvent member has an explicit case — a
// future generated member without an arm makes this a `tsc -b` compile error;
// at runtime it throws so a malformed wire event can never silently no-op.
function assertNever(event: never): never {
  throw new Error(`Unhandled StreamEvent: ${JSON.stringify(event)}`);
}

export interface UiMessage {
  id: string;
  role: Role;
  content: string;
  toolTraces: ToolTrace[];
  citations: PersistedCitationRef[];
  createdAt: number;
  cost_usd?: number;
  terminated_reason?: string | null;
  error?: { code: string; detail: string } | null;
  // Keyed by call_site so planner / decomposer / synthesizer / synthesizer.grouped
  // each contribute a dedicated row in AgentTimeline (ADR-095 Phase 1).
  // Last-wins per call_site (CRAG retries / multi-hop re-plans).
  reasoning?: Record<string, ReasoningState>;
  // ADR-092 Component A — verifier-gated iterative sync loop (default OFF).
  // Keyed by iteration (1-indexed: 1=initial pass, 2=first retry, ...) so
  // multi-iteration loops render one row per pass in AgentTimeline. Each
  // Started/Complete event PAIR populates one entry. Mirrors the
  // Record<string, ReasoningState> shape established for #6 thinking-mode
  // visibility (commits 26a5a3ae + 02ff0441 per AGENTS.md narrative).
  verifier?: Record<number, VerifierState>;
  // ADR-092 Component A — verifier-gated iterative sync loop (default OFF).
  // Set when backend yields RetryStartedEvent (low faithfulness verifier
  // rejection + iteration budget remaining); current/max iteration + reason
  // surface for an optional "Revising answer (N/M)..." UI indicator. Null
  // when no retry has fired on this message.
  retryState?: {
    iteration: number;
    maxIterations: number;
    reason: string;
    supportedFraction: number;
  } | null;
  // Iter 14 F6 — final verifier verdict (distinct from per-iteration
  // verifier Record above). Set ONCE after the backend verifier loop
  // terminates. AgentTimeline renders this as a subtle tail badge.
  // Null when verifier_loop is OFF (default) or no verdict event seen.
  verifierVerdict?: {
    supportedFraction: number;
    floor: number;
    accepted: boolean;
    iteration: number;
  } | null;
  // ADR-104 v3 (T7.2) — deep-research worker tree. Keyed by worker_id so the
  // out-of-order WorkerStatusEvents (concurrent workers interleave) upsert
  // last-wins per worker. Undefined on v2 runs (no worker_status events) →
  // DeepResearchPanel renders null, leaving the v2 experience untouched.
  workers?: Record<string, WorkerState>;
  // ADR-104 v3 (T7.2) — the lead's PlanEvent ToolPlan (rationale surfaced as
  // the research-plan header). Undefined until a `plan` event arrives.
  plan?: ToolPlan | null;
  // Workstream A/G2 — answer spans whose claims the P-Cite entailment gate could
  // NOT bind to any source. Set by the additive `unverified_claims` event, which
  // the backend emits ONLY when v3_unverified_claim_flagging_enabled is ON
  // (default OFF) → undefined on every current run. MessageRenderer surfaces
  // these as a subtle inline "unverified" marker.
  unverifiedClaims?: UnverifiedClaimRef[];
  // ADR-123 D4 — retrieval PROVENANCE, not support. Emitted only when P-Cite bound
  // nothing, behind `v3_documents_consulted_enabled` (default OFF → undefined on
  // every current run). Never render it as a citation.
  documentsConsulted?: ConsultedDocRef[];
}

export interface WorkerState {
  workerId: string;
  status: WorkerStatus;
  facet: string;
  summary: string;
  startedAt: number;
  updatedAt: number;
}

export interface ReasoningState {
  callSite: string;
  effort: string;
  startedAt: number;
  durationMs?: number;
  tokens?: number;
}

export interface VerifierState {
  iteration: number;
  maxIterations: number;
  supportedFractionFloor: number;
  startedAt: number;
  supportedFraction?: number;
  accepted?: boolean;
  durationMs?: number;
}

interface ChatState {
  // Active thread
  activeConversationId: string | null;
  activeTenantId: string | null;
  messages: UiMessage[];
  isStreaming: boolean;
  // Decouple-input-from-citations: the answer_ready event sets this true the
  // instant the answer TEXT is done (BEFORE the post-hoc citation gate). The
  // composer re-enables on (isStreaming && answerReady) while the message keeps
  // receiving late citation + complete events. Reset on stream start + every
  // terminal transition.
  answerReady: boolean;
  // Distinguishes a fresh-stream session from a HISTORY replay invoked
  // via loadJobFromHistory → pumpJobReplay. Both paths set isStreaming=true
  // (via startAssistantMessage) to disable the Send button while events
  // are being received, but the AgentTimeline must NOT render the active
  // shimmer animation during a replay — the job already completed minutes
  // ago and the user clicked the sidebar entry to RESTORE that final state.
  // Without this flag, the synthesizing dot pulses for ~30s as the SSE
  // relay re-delivers PG event log events one-by-one, and operator
  // perceives this as "Synthesizing still running after answers are
  // generated". AgentTimeline reads this flag and derives isComplete to
  // include `isReplaying === true` so all stage dots render as "done"
  // throughout the replay window.
  isReplaying: boolean;
  currentRunId: string | null;
  abortController: AbortController | null;
  // ADR-091 Phase 5b.2: active async job_id for cooperative cancel.
  // NULL when sync /v1/query/stream is in use (no job_id to cancel).
  activeJobId: string | null;
  reconnectState: { attempt: number; lastEventId: number } | null;
  // Defect #3 watchdog tracking (2026-06-02 click-only e2e audit):
  // updated to Date.now() on every successful applyEvent dispatch +
  // on startAssistantMessage. Read by useStreamWatchdog to force
  // endStream() when no events have arrived for > 120s while
  // isStreaming is true (catches lost `complete` events on SSE relay
  // close races or worker bugs that the bake-frozen rag-query can't
  // be redeployed to fix). Null when no stream is active.
  lastEventAt: number | null;
  activeArtifactMessageId: string | null;
  // CitationDetailPanel drill-down (right-side drawer). Set by the CitationChip
  // click handler — since the Sources card was deleted (2026-08-09) that inline
  // pill is the ONLY writer; consumed by CitationDetailPanel.
  // Stores chunk_id (string) NOT index (number) per the 10th-iteration
  // structural fix: backend may emit MULTIPLE CitationEvents with the
  // SAME chunk_id but DIFFERENT raw indices (each [N] occurrence in
  // synth output produces its own CitationEvent post-Jaccard-rebinding).
  // Pre-10th-iteration, click-dispatch was the raw index — but the
  // index-key lookup chain (Sources-card canonical override → chip raw
  // idx → findCitation(c.index === idx)) could resolve to DIFFERENT
  // sibling cites: the card displayed the image-wins cite (e.g. idx=7
  // with image), dispatched canonical idx=4, and the panel found a
  // DIFFERENT idx=4 sibling (no image) — card showed thumbnail, panel
  // showed placeholder. chunk_id is the only stable identifier of
  // "this underlying chunk of evidence" across same-chunk siblings.
  // Null when no panel open.
  activeCitationChunkId: string | null;
  composerDraft: string;
  composerFocusSignal: number;
  highlightedSourceIdx: number | null;

  // Mutators
  setActiveTenant: (tenantId: string) => void;
  setActiveConversation: (convId: string | null) => void;
  // Iter 22 — idempotent get-or-create. When activeConversationId is null,
  // generates a fresh v4 UUID, sets it on the store, and returns it. When
  // activeConversationId is already set, returns it unchanged. CRITICAL
  // DISTINCTION vs setActiveConversation: this action does NOT reset
  // messages. setActiveConversation is for the sidebar-restore path
  // (loadJobFromHistory → wipe thread → rebuild from snapshot);
  // ensureActiveConversation is for the first-submit-in-fresh-thread path
  // (Composer needs a stable UUID to send to the backend WITHOUT wiping the
  // user message it just appended). Closes iter 20 + iter 21 silent-inert
  // defect where Composer sent `conversation_id: undefined` → backend stored
  // as NULL → runtime multiturn fetch unreachable.
  ensureActiveConversation: () => string;
  loadMessages: (msgs: UiMessage[]) => void;
  appendUserMessage: (text: string) => UiMessage;
  startAssistantMessage: () => UiMessage;
  applyEvent: (event: StreamEvent) => void;
  noteStreamActivity: () => void;
  setAbortController: (c: AbortController | null) => void;
  setActiveJobId: (jobId: string | null) => void;
  setReconnectState: (s: { attempt: number; lastEventId: number } | null) => void;
  setActiveArtifactMessageId: (id: string | null) => void;
  setActiveCitationChunkId: (chunkId: string | null) => void;
  setComposerDraft: (text: string) => void;
  requestComposerFocus: () => void;
  setHighlightedSourceIdx: (idx: number | null) => void;
  endStream: () => void;
  clear: () => void;
  // Sprint E #1 — sidebar click on completed job restores the conversation
  // into main pane (not drawer). Wipes thread, appends user query +
  // assistant slot, then SSE-replays the job's PG event log via
  // ssePumpJobReplay (lastEventId=0 → full replay). All events flow
  // through applyEvent unchanged.
  loadJobFromHistory: (
    params: { tenantId: string; jobId: string; conversationId: string | null; queryText: string },
    token: string,
  ) => Promise<void>;
  loadConversationFromHistory: (
    params: {
      tenantId: string;
      conversationId: string | null;
      // Rendered in THIS order, so it must be oldest-first — the order
      // `groupJobsByConversation` already returns. A reversed array renders the
      // conversation backwards with no error anywhere.
      turns: readonly HistoryTurn[];
    },
    token: string,
  ) => Promise<void>;
}

export interface HistoryTurn {
  jobId: string;
  queryText: string;
}

let _idSeq = 0;
function nextId(): string {
  _idSeq += 1;
  return `m_${Date.now()}_${_idSeq}`;
}

/**
 * Iter 22 — client-side thread-grouping UUID flowing into
 * query_jobs.request_jsonb.conversation_id at POST time (per ADR-091 §3.3).
 *
 * Math.random fallback (Vercel ai-chatbot pattern, lib/utils.ts) over
 * crypto.randomUUID() because:
 *   (a) crypto.randomUUID requires a SECURE CONTEXT (HTTPS or localhost);
 *       silently undefined in some jsdom configurations.
 *   (b) This UUID is NOT a security token — the backend validates the JWT,
 *       not the UUID. Math.random v4 is sufficient for thread-grouping.
 *   (c) Zero npm deps — uuid@^9 would be ~3 KB gzipped for the same effect.
 */
function generateConversationId(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const useChatStore = create<ChatState>((set, get) => ({
  activeConversationId: null,
  activeTenantId: null,
  messages: [],
  isStreaming: false,
  answerReady: false,
  isReplaying: false,
  currentRunId: null,
  abortController: null,
  activeJobId: null,
  reconnectState: null,
  lastEventAt: null,
  activeArtifactMessageId: null,
  activeCitationChunkId: null,
  composerDraft: "",
  composerFocusSignal: 0,
  highlightedSourceIdx: null,

  setActiveTenant: (tenantId) => set({ activeTenantId: tenantId }),

  setActiveConversation: (convId) => set({ activeConversationId: convId, messages: [] }),

  ensureActiveConversation: () => {
    const existing = get().activeConversationId;
    if (existing) return existing;
    const newId = generateConversationId();
    set({ activeConversationId: newId });
    return newId;
  },

  loadMessages: (msgs) => set({ messages: msgs }),

  appendUserMessage: (text) => {
    const msg: UiMessage = {
      id: nextId(),
      role: "user",
      content: text,
      toolTraces: [],
      citations: [],
      createdAt: Date.now(),
    };
    set((s) => ({ messages: [...s.messages, msg] }));
    return msg;
  },

  startAssistantMessage: () => {
    const msg: UiMessage = {
      id: nextId(),
      role: "assistant",
      content: "",
      toolTraces: [],
      citations: [],
      createdAt: Date.now(),
    };
    set((s) => ({
      messages: [...s.messages, msg],
      isStreaming: true,
      answerReady: false,
      lastEventAt: Date.now(),
    }));
    return msg;
  },

  applyEvent: (event) => {
    const last = get().messages[get().messages.length - 1];
    if (!last || last.role !== "assistant") return;

    set((s) => {
      const msgs = [...s.messages];
      const idx = msgs.length - 1;
      const m = { ...msgs[idx] };
      const reconnectClear = s.reconnectState !== null ? { reconnectState: null } : {};

      // Defect #3 (2026-06-02 click-only e2e audit): every dispatched
      // event resets the watchdog clock. Read by useStreamWatchdog to
      // detect a stuck `isStreaming=true` state when no events have
      // arrived for > 120s (lost `complete`, SSE relay close race,
      // worker bug not reachable via redeploy of the bake-frozen
      // rag-query Container App).
      const nowTs = Date.now();
      switch (event.type) {
        case "plan":
          // ADR-104 v3: the lead's ToolPlan rationale feeds DeepResearchPanel.
          m.plan = event.plan;
          msgs[idx] = m;
          return { ...s, messages: msgs, currentRunId: event.run_id, lastEventAt: nowTs };
        case "tool_call_start": {
          const trace: ToolTrace = {
            call_id: event.call_id,
            tool: event.tool,
            args_redacted: event.args,
            result_summary: "",
            duration_ms: 0,
            cost_usd: 0,
          };
          m.toolTraces = [...m.toolTraces, trace];
          break;
        }
        case "tool_call_end": {
          m.toolTraces = m.toolTraces.map((t) =>
            t.call_id === event.call_id ? { ...t, result_summary: event.result_summary, duration_ms: event.ms } : t,
          );
          break;
        }
        case "thinking":
          // Deliberate no-op: the raw `thinking` delta stream is intentionally
          // NOT surfaced here (reasoning is shown via reasoning_started/complete
          // + AgentTimeline). Explicit arm keeps the switch exhaustive.
          break;
        case "reasoning_started": {
          const next = { ...(m.reasoning ?? {}) };
          next[event.call_site] = {
            callSite: event.call_site,
            effort: event.reasoning_effort,
            startedAt: Date.now(),
          };
          m.reasoning = next;
          break;
        }
        case "reasoning_complete": {
          const next = { ...(m.reasoning ?? {}) };
          const existing = next[event.call_site];
          if (existing) {
            next[event.call_site] = {
              ...existing,
              durationMs: event.duration_ms,
              tokens: event.reasoning_tokens,
            };
          } else {
            // No Started event seen for this call_site (non-synth call sites
            // like planner + decomposer don't emit Started — they're
            // non-streaming so we only learn duration after the call returns).
            // Backstop: synthesize the state from duration + a "medium" effort
            // fallback (planner + decomposer both run at retrieval effort).
            next[event.call_site] = {
              callSite: event.call_site,
              effort: "medium",
              startedAt: Date.now() - event.duration_ms,
              durationMs: event.duration_ms,
              tokens: event.reasoning_tokens,
            };
          }
          m.reasoning = next;
          break;
        }
        case "token":
          m.content = m.content + event.delta;
          break;
        case "answer_ready":
          // Answer TEXT done; keep isStreaming TRUE so late citation + complete
          // events still route to this message + the watchdog keeps guarding.
          // The composer re-enables via the (isStreaming && answerReady) gate.
          return { ...s, answerReady: true, lastEventAt: nowTs, ...reconnectClear };
        case "citation":
          // ADR-095 Phase 6 — Perplexity-Plus bundle. KEEP EVERY citation
          // here. The Sprint E #3 dispatch-time dedup (drop same-chunk
          // events) silently broke the [N] → chip mapping when backend
          // synthesizer.py emitted indices 8, 10, 11, 12 all rebinding to
          // the same canonical chunk (wire-pickling test query → SOURCES
          // 47 collapsed to 17 unique chunks): MessageRenderer's
          // citationsByIndex.get(10) missed, [10] stayed as plain text,
          // and the CitationDetailPanel never opened on chip click
          // (Defects #1 + #2 in the 2026-06-02 e2e audit). Dedup MOVED
          // to render time (Map<chunk_id, first-wins>) so every [N] marker
          // resolves to its CitationRef. The second reason — one card per
          // unique chunk in the Sources strip — died with that card (2026-08-09).
          m.citations = [...m.citations, event.citation];
          break;
        case "unverified_claims":
          // Workstream A/G2 — additive faithfulness surface. Records the answer
          // spans whose claims the P-Cite entailment gate could NOT bind to any
          // source; MessageRenderer renders a subtle inline "unverified" marker.
          // Emitted ONLY when v3_unverified_claim_flagging_enabled is ON
          // (default OFF) → inert by default. Spread-preserved through terminal
          // hydration so it survives loadJobFromHistory replay.
          m.unverifiedClaims = event.claims;
          break;
        case "documents_consulted":
          // ADR-123 D4 — the documents the workers read, emitted ONLY when P-Cite
          // bound nothing. Recorded on the message like unverifiedClaims (spread-
          // preserved through terminal hydration); Message.tsx renders it as a
          // sibling of the answer, never through the citation path.
          m.documentsConsulted = event.documents;
          break;
        case "worker_status": {
          // ADR-104 v3: key by worker_id so out-of-order concurrent events
          // upsert last-wins. The wire populates facet only on "spawned" and
          // summary only on "done"/"failed" — fall back to the prior value so
          // an empty field on a later event never erases an established one.
          const next = { ...(m.workers ?? {}) };
          const prev = next[event.worker_id];
          next[event.worker_id] = {
            workerId: event.worker_id,
            status: event.status,
            facet: event.facet || prev?.facet || "",
            summary: event.summary || prev?.summary || "",
            startedAt: prev?.startedAt ?? nowTs,
            updatedAt: nowTs,
          };
          m.workers = next;
          break;
        }
        case "retry_started":
          // ADR-092 Component A — verifier-gated iterative sync loop. The
          // backend rejected the just-streamed answer (faithfulness floor
          // not met) and is re-entering the retrieval loop. Wipe content
          // + citations so the user sees a fresh stream for the next
          // attempt. Cowork-style "let me think again" UX (Phase 1
          // Option B per ADR-092 operator pick).
          //
          // Pure additive dispatch — when the backend flag
          // `agent_sync_verifier_loop_enabled` is OFF (default), no
          // RetryStartedEvent is ever emitted so this branch is dead
          // code in production. Default-OFF discipline matches
          // temporal_decay_enabled / semantic_cache_enabled.
          m.content = "";
          m.citations = [];
          m.retryState = {
            iteration: event.iteration,
            maxIterations: event.max_iterations,
            reason: event.reason,
            supportedFraction: event.supported_fraction,
          };
          msgs[idx] = m;
          return { ...s, messages: msgs, answerReady: false, ...reconnectClear, lastEventAt: nowTs };
        case "verifier_started": {
          const next = { ...(m.verifier ?? {}) };
          next[event.iteration] = {
            iteration: event.iteration,
            maxIterations: event.max_iterations,
            supportedFractionFloor: event.supported_fraction_floor,
            startedAt: Date.now(),
          };
          m.verifier = next;
          break;
        }
        case "verifier_verdict": {
          m.verifierVerdict = {
            supportedFraction: event.supported_fraction,
            floor: event.floor,
            accepted: event.accepted,
            iteration: event.iteration,
          };
          break;
        }
        case "verifier_complete": {
          const next = { ...(m.verifier ?? {}) };
          const existing = next[event.iteration];
          if (existing) {
            next[event.iteration] = {
              ...existing,
              supportedFraction: event.supported_fraction,
              accepted: event.accepted,
              durationMs: event.duration_ms,
            };
          } else {
            // Backstop: no verifier_started seen for this iteration (e.g.
            // SSE relay reconnect mid-verifier; Last-Event-ID resume landed
            // past the Started event boundary). Mirrors the
            // reasoning_complete backstop from commit 02ff0441 — synthesize
            // a usable state from duration alone so the row still renders
            // as "Verified/Rejected · 0.XX". supportedFractionFloor=0.7 is
            // a safe default that does not surface in the accepted/rejected
            // text path; the details panel shows it only when expanded.
            next[event.iteration] = {
              iteration: event.iteration,
              maxIterations: event.iteration,
              supportedFractionFloor: 0.7,
              startedAt: Date.now() - event.duration_ms,
              supportedFraction: event.supported_fraction,
              accepted: event.accepted,
              durationMs: event.duration_ms,
            };
          }
          m.verifier = next;
          break;
        }
        case "complete":
          // Defect #3 (2026-06-02 click-only e2e audit): operator saw the
          // Stop button + AgentTimeline pulse remain active for >40s
          // AFTER the answer fully streamed. Pre-fix this case cleared
          // ONLY isStreaming + currentRunId, leaving abortController +
          // activeJobId + reconnectState dangling. If any of those
          // selectors gated a UI element (or if React batched the
          // partial set such that the consumer never saw the
          // isStreaming=false transition), the user-visible state
          // stuck. Defensive: mirror the FULL endStream() semantics
          // here so terminal events leave the same atomic post-state
          // as user-driven abort.
          m.cost_usd = event.cost_usd;
          m.terminated_reason = event.terminated_reason ?? null;
          msgs[idx] = m;
          return {
            ...s,
            messages: msgs,
            isStreaming: false,
            answerReady: false,
            currentRunId: null,
            abortController: null,
            activeJobId: null,
            reconnectState: null,
            lastEventAt: nowTs,
          };
        case "error":
          m.error = { code: event.code, detail: event.detail };
          msgs[idx] = m;
          return {
            ...s,
            messages: msgs,
            isStreaming: false,
            answerReady: false,
            currentRunId: null,
            abortController: null,
            activeJobId: null,
            reconnectState: null,
            lastEventAt: nowTs,
          };
        default:
          return assertNever(event);
      }
      msgs[idx] = m;
      return { ...s, messages: msgs, ...reconnectClear, lastEventAt: nowTs };
    });
  },

  setAbortController: (c) => set({ abortController: c }),

  setActiveJobId: (jobId) => set({ activeJobId: jobId }),

  setReconnectState: (s) => set({ reconnectState: s }),

  setActiveArtifactMessageId: (id) => set({ activeArtifactMessageId: id }),

  setActiveCitationChunkId: (chunkId) => set({ activeCitationChunkId: chunkId }),

  setComposerDraft: (text) => set({ composerDraft: text }),

  requestComposerFocus: () => set((s) => ({ composerFocusSignal: s.composerFocusSignal + 1 })),

  setHighlightedSourceIdx: (idx) => set({ highlightedSourceIdx: idx }),

  // ADR-098 Phase-4 watchdog fix: keepalive frames are transport-liveness.
  // subscribeJobStream calls this on EVERY received frame so useStreamWatchdog
  // won't kill a long-silent-reasoning stream while `: keepalive` still flows;
  // the isStreaming guard stops a late keepalive from resurrecting a dead timer.
  noteStreamActivity: () => set((s) => (s.isStreaming ? { lastEventAt: Date.now() } : {})),

  endStream: () =>
    set({
      isStreaming: false,
      answerReady: false,
      isReplaying: false,
      abortController: null,
      currentRunId: null,
      activeJobId: null,
      reconnectState: null,
      lastEventAt: null,
    }),

  clear: () =>
    set({
      activeConversationId: null,
      messages: [],
      isStreaming: false,
      answerReady: false,
      isReplaying: false,
      currentRunId: null,
      abortController: null,
      activeJobId: null,
      reconnectState: null,
      lastEventAt: null,
      activeArtifactMessageId: null,
      activeCitationChunkId: null,
      composerDraft: "",
    }),

  loadJobFromHistory: async ({ tenantId, jobId, conversationId, queryText }, token) =>
    // A lone job IS a one-turn conversation. Delegating instead of duplicating
    // means the single-turn path cannot drift from the multi-turn one.
    get().loadConversationFromHistory({ tenantId, conversationId, turns: [{ jobId, queryText }] }, token),

  loadConversationFromHistory: async ({ tenantId, conversationId, turns }, token) => {
    // Iter 19 — snapshot-based terminal-job history closes iter 18 defect 2
    // structurally. For terminal jobs (completed/failed/cancelled/
    // dead_lettered) the final answer + citations + cost are ALREADY
    // persisted to tenant_<id>.query_jobs at completion via worker.
    // _persist_completion. Worker uses a LOCAL citations.append(event.
    // citation) accumulator OUTSIDE any silent-emit swallow block — so
    // query_jobs.citations_jsonb is the structural floor of truth.
    // Reading from this snapshot makes iter 18 defect 2 (sidebar replay
    // drops m.citations) STRUCTURALLY IMPOSSIBLE for terminal jobs.
    // Active jobs (queued/running/planning/retrieving/synthesizing/
    // verifying) have no final answer yet — they go through the
    // existing pumpJobReplay (subscribeJobStream Last-Event-ID=0 +
    // PG replay + Redis Stream tail per ADR-091 Phase 3) for live
    // progress. Snapshot fetch failure (404 race, network blip) falls
    // back to pumpJobReplay defensively.
    set({
      messages: [],
      isStreaming: false,
      answerReady: false,
      isReplaying: false,
      currentRunId: null,
      abortController: null,
      activeJobId: null,
      reconnectState: null,
      lastEventAt: null,
      activeArtifactMessageId: null,
      activeCitationChunkId: null,
      activeTenantId: tenantId,
      activeConversationId: conversationId,
    });
    if (turns.length === 0) return;

    // Fetch every turn CONCURRENTLY — a 3-turn conversation must not pay three
    // serial round-trips before the thread appears. The per-turn catch keeps one
    // unreachable turn from rejecting the whole conversation.
    const snapshots = await Promise.all(
      turns.map((turn) =>
        getJob(tenantId, turn.jobId, token).catch((err): JobSnapshot | null => {
          // eslint-disable-next-line no-console
          console.warn("[history] getJob failed for turn; rendering it empty", turn.jobId, err);
          return null;
        }),
      ),
    );

    // ...but RENDER sequentially. applyEvent and the terminal hydration below
    // both write to messages[messages.length - 1], so a turn's events can only
    // reach its own assistant message while that message is the last one.
    // Rendering in order IS the per-turn isolation guarantee — interleave the
    // turns and every citation lands on whichever message happened to be last.
    for (let i = 0; i < turns.length; i += 1) {
      const turn = turns[i];
      const snapshot = snapshots[i];
      const isLastTurn = i === turns.length - 1;

      get().appendUserMessage(turn.queryText);

      // NOT unconditional: `pumpJobReplay` opens its OWN assistant message
      // (ssePumpJobReplay.ts:45), so starting one here too stacked an empty
      // bubble above the live one on a still-running last turn. Each branch
      // below now opens exactly the message it will write to.
      if (snapshot && isTerminal(snapshot.status)) {
        get().startAssistantMessage();
        // ADR-104 history==live parity: replay the persisted v3 trace through the
        // SAME applyEvent reducer. MUST precede the content/citation hydrate below
        // so that hydrate (a message spread) is the FINAL write of the citation
        // floor — the trace can never clobber it (defense-in-depth backstop).
        for (const ev of snapshot.research_trace_events ?? []) {
          get().applyEvent(ev);
        }
        // Terminal hydration: mirrors applyEvent case "complete" mutation shape
        // (lines 386-410) for atomic post-state symmetry. Iter 19 invariant:
        // every chatStore field case "complete" touches MUST be touched here
        // too — future cleanups that consolidate atomic-state-reset MUST
        // preserve both surfaces in lockstep (pinned by
        // chatStore.loadJobFromHistory.test.ts symmetry assertions).
        const finalSnapshot = snapshot;
        set((s) => {
          const msgs = [...s.messages];
          const idx = msgs.length - 1;
          if (idx < 0 || msgs[idx].role !== "assistant") return s;
          const m = { ...msgs[idx] };
          m.content = finalSnapshot.answer_text ?? "";
          m.citations = narrowCitations(finalSnapshot.citations_jsonb);
          m.cost_usd = finalSnapshot.cost_usd_total;
          m.terminated_reason = finalSnapshot.terminated_reason ?? null;
          if (finalSnapshot.status === "failed" && finalSnapshot.error_detail) {
            m.error = { code: "snapshot_failed", detail: finalSnapshot.error_detail };
          }
          msgs[idx] = m;
          return {
            ...s,
            messages: msgs,
            isStreaming: false,
            answerReady: false,
            isReplaying: false,
            currentRunId: null,
            abortController: null,
            activeJobId: null,
            reconnectState: null,
            lastEventAt: null,
          };
        });
        continue;
      }

      // Only the LAST turn may go live. Every earlier turn is terminal by
      // definition, and pumping one would append its assistant message AFTER
      // the turns that follow it — reordering the conversation.
      if (isLastTurn) {
        // The pump owns the assistant message for a live turn — see above.
        const { pumpJobReplay } = await import("./ssePumpJobReplay");
        await pumpJobReplay({ tenantId, jobId: turn.jobId, conversationId }, token);
        return;
      }

      // An earlier turn we could not hydrate: its snapshot was unreachable, or
      // it is somehow still running. Render it as a visible empty answer rather
      // than dropping it — a dropped turn makes the sidebar's turn-count badge
      // lie in the opposite direction from the defect this loop fixes.
      // An unhydratable earlier turn still needs a visible (empty) answer, so
      // it opens its own message here rather than relying on the removed
      // unconditional call.
      get().startAssistantMessage();
      const unreachable = snapshot === null;
      set((s) => {
        const msgs = [...s.messages];
        const idx = msgs.length - 1;
        if (idx < 0 || msgs[idx].role !== "assistant") return s;
        if (unreachable) {
          msgs[idx] = {
            ...msgs[idx],
            error: {
              code: "snapshot_unavailable",
              detail: `This turn could not be loaded (job ${turn.jobId}).`,
            },
          };
        }
        return {
          ...s,
          messages: msgs,
          isStreaming: false,
          answerReady: false,
          isReplaying: false,
          currentRunId: null,
          abortController: null,
          activeJobId: null,
          reconnectState: null,
          lastEventAt: null,
        };
      });
    }
  },
}));

export const useMessages = () => useChatStore((s) => s.messages);
export const useIsStreaming = () => useChatStore((s) => s.isStreaming);
export const useAnswerReady = () => useChatStore((s) => s.answerReady);
export const useActiveTenantId = () => useChatStore((s) => s.activeTenantId);
export const useAbort = () => useChatStore((s) => s.abortController);
